import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  X,
  Upload,
  Loader2,
  CheckCircle,
  Trash2,
  Image as ImageIcon,
  Heading1,
  Heading2,
  List,
  ListOrdered,
} from "lucide-react";

export default function BlogAdmin() {
  const [blogs, setBlogs] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [form, setForm] = useState({
    title: "",
    summary: "",
    content: "",
    category: "Architecture",
    author: "",
  });

  const contentRef = useRef(null);

  const fetchBlogs = async () => {
    try {
      setFetchLoading(true);
      const res = await axios.get("https://archpoint.onrender.com/api/blogs");
      setBlogs(res.data);
    } catch (err) {
      showNotification("Failed to fetch blogs", "error");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showNotification("Image size should be less than 5MB", "error");
        return;
      }
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  // Inserts a formatting marker (heading/subheading/bullet/numbered) into the
  // content textarea at the current cursor position, on its own line, and
  // keeps the cursor positioned right after the marker so the user can
  // start typing immediately. This is stored as plain text inside
  // `content` (no backend/schema changes needed) and later parsed by the
  // blog detail page.
  const insertFormatting = (marker) => {
    const textarea = contentRef.current;
    const value = form.content;

    if (!textarea) {
      // Fallback: just append if the ref isn't ready for some reason
      setForm((f) => ({
        ...f,
        content: f.content ? `${f.content}\n${marker}` : marker,
      }));
      return;
    }

    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const selected = value.slice(start, end);

    const before = value.slice(0, start);
    const after = value.slice(end);
    const needsNewlineBefore = before.length > 0 && !before.endsWith("\n");
    const insertText = `${needsNewlineBefore ? "\n" : ""}${marker}${selected}`;
    const newValue = `${before}${insertText}${after}`;

    setForm((f) => ({ ...f, content: newValue }));

    requestAnimationFrame(() => {
      textarea.focus();
      const cursorPos = before.length + insertText.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const validateForm = () => {
    if (!form.title.trim()) {
      showNotification("Title is required", "error");
      return false;
    }
    if (!form.author.trim()) {
      showNotification("Author is required", "error");
      return false;
    }
    if (!form.summary.trim()) {
      showNotification("Summary is required", "error");
      return false;
    }
    if (!form.content.trim()) {
      showNotification("Content is required", "error");
      return false;
    }
    if (!image) {
      showNotification("Please select an image", "error");
      return false;
    }
    return true;
  };

  const submitHandler = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("image", image);

      await axios.post("https://archpoint.onrender.com/api/blogs", fd);

      setForm({
        title: "",
        summary: "",
        content: "",
        category: "Architecture",
        author: "",
      });
      setImage(null);
      setImagePreview(null);
      showNotification("Blog published successfully!");
      fetchBlogs();
    } catch (err) {
      showNotification(err.response?.data?.message || "Upload failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    try {
      await axios.delete(`https://archpoint.onrender.com/api/blogs/${id}`);
      showNotification("Blog deleted successfully!");
      fetchBlogs();
    } catch (err) {
      showNotification("Failed to delete blog", "error");
    }
  };

  const toolbarButtons = [
    { label: "Heading", icon: Heading1, marker: "## " },
    { label: "Subheading", icon: Heading2, marker: "### " },
    { label: "Bullet", icon: List, marker: "• " },
    { label: "Numbered", icon: ListOrdered, marker: "1. " },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-slate-50">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-6 py-3 rounded-lg shadow-lg animate-slide-in ${
          notification.type === "success"
            ? "bg-green-500 text-white"
            : "bg-red-500 text-white"
        }`}>
          {notification.type === "success" && <CheckCircle size={20} />}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto p-6 md:p-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Blog Admin Dashboard</h1>
          <p className="text-slate-600">Create and manage your blog posts</p>
        </div>

        {/* Create Blog Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 mb-12 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
            <h2 className="text-2xl font-bold text-white">Create New Blog Post</h2>
          </div>

          <div className="p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Blog Title *
                </label>
                <input
                  placeholder="Enter blog title"
                  className="border border-slate-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Author Name *
                </label>
                <input
                  placeholder="Enter author name"
                  className="border border-slate-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Category *
              </label>
              <select
                className="border border-slate-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option>Architecture</option>
                <option>Interior</option>
                <option>Urban Design</option>
                <option>Construction</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Summary *
              </label>
              <textarea
                placeholder="Write a brief summary of your blog post"
                className="border border-slate-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-amber-500 focus:border-transparent transition resize-none"
                rows="3"
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
              />
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Full Content *
                </label>
              </div>

              {/* Formatting toolbar — inserts plain-text markers into the
                  content field itself, so no backend/schema change is
                  needed. The blog detail page parses these markers back
                  into styled headings/subheadings/lists. */}
              <div className="flex flex-wrap gap-2 mb-2">
                {toolbarButtons.map(({ label, icon: Icon, marker }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => insertFormatting(marker)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-amber-100 hover:text-amber-700 rounded-md transition"
                    title={`Insert ${label}`}
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                ))}
              </div>

              <textarea
                ref={contentRef}
                placeholder="Write the complete blog content here"
                rows="10"
                className="border border-slate-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-amber-500 focus:border-transparent transition resize-none font-mono text-sm"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Tip: use the buttons above, or type <code className="bg-slate-100 px-1 rounded">## </code> for a heading,{" "}
                <code className="bg-slate-100 px-1 rounded">### </code> for a subheading,{" "}
                <code className="bg-slate-100 px-1 rounded">• </code> for a bullet, and{" "}
                <code className="bg-slate-100 px-1 rounded">1. </code> for a numbered point, each on its own line.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Featured Image *
              </label>

              {!imagePreview ? (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-10 h-10 mb-3 text-slate-400" />
                    <p className="mb-2 text-sm text-slate-600">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-slate-500">PNG, JPG or WEBP (MAX. 5MB)</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
              ) : (
                <div className="relative w-full h-64 rounded-lg overflow-hidden border-2 border-slate-300">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={submitHandler}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Publishing...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Publish Blog Post
                </>
              )}
            </button>
          </div>
        </div>

        {/* Blog List */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Published Blogs</h2>
        </div>

        {fetchLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="animate-spin text-amber-500" size={48} />
          </div>
        ) : blogs.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12 text-center">
            <ImageIcon className="mx-auto mb-4 text-slate-400" size={64} />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No blogs yet</h3>
            <p className="text-slate-500">Create your first blog post to get started</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((b) => (
              <div
                key={b._id}
                className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={b.imageUrl}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    alt={b.title}
                  />
                  <div className="absolute top-3 right-3">
                    <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {b.category}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <h4 className="font-bold text-lg text-slate-800 mb-2 line-clamp-2">{b.title}</h4>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{b.summary}</p>
                  <p className="text-xs text-slate-500 mb-4">By {b.author}</p>

                  <button
                    onClick={() => deleteBlog(b._id)}
                    className="w-full flex items-center justify-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition font-medium"
                  >
                    <Trash2 size={16} />
                    Delete Blog
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}